import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import {
  formatRating,
  getCellStyle,
  getHeaderStyle,
  tableBaseStyle,
  tableCardHeaderStyle,
  tableCardStyle,
  tableScrollStyle,
  type ScorecardHistoryRow,
} from './styles';

/**
 * React renderer of the offer-approval "Scorecard History" table.
 *
 * The note is stored as static HTML (`generateScorecardTableHTML`) so it
 * survives in emails / older clients, but in browser contexts (e.g. the
 * in-app notification center) `HtmlRenderer` swaps the static markup for
 * this component via Interweave's `transform`. That gives the Activity
 * column a real Kula tooltip (`TruncatedTextWithTooltip`) instead of the
 * native browser one.
 */

const activityCellMaxWidth = '320px';

export const ScorecardHistoryTable = ({
  scorecards,
}: {
  scorecards: ScorecardHistoryRow[];
}) => {
  if (!scorecards || scorecards.length === 0) {
    return null;
  }
  return (
    <div style={tableScrollStyle}>
      <div style={tableCardStyle}>
        <div style={tableCardHeaderStyle}>Scorecard History</div>
        <table style={tableBaseStyle}>
          <thead>
            <tr>
              <th style={getHeaderStyle(true, false)}>Interviewer/Reviewer</th>
              <th style={getHeaderStyle(false, false)}>Rating</th>
              <th style={getHeaderStyle(false, false)}>Activity</th>
              <th style={getHeaderStyle(false, true)}>Scorecard URL</th>
            </tr>
          </thead>
          <tbody>
            {scorecards.map((sc, idx) => {
              const isLastRow = idx === scorecards.length - 1;
              // `scorecard_url` encodes the scorecard id and is unique per row;
              // fall back to the index when a row has no link.
              const rowKey = sc.scorecard_url ?? idx;
              return (
                <tr key={rowKey}>
                  <td style={getCellStyle(true, false, isLastRow)}>
                    {sc.reviewer || '-'}
                  </td>
                  <td style={getCellStyle(false, false, isLastRow)}>
                    {formatRating(sc.rating)}
                  </td>
                  <td
                    style={{
                      ...getCellStyle(false, false, isLastRow),
                      maxWidth: activityCellMaxWidth,
                    }}
                  >
                    {sc.activity ? (
                      <TruncatedTextWithTooltip
                        text={sc.activity}
                        placement="top"
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td style={getCellStyle(false, true, isLastRow)}>
                    {sc.scorecard_url ? (
                      <a
                        href={sc.scorecard_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#5C53C9' }}
                      >
                        Link
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
